import type { Row } from '@tanstack/react-table';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import styled from 'styled-components';

import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { TableCell } from '@/components/ui/table';

import { DetailFormStatusValue } from 'components/DetailsView';
import { SITE_CONTRACT_TYPES_OPTIONS } from 'pages/Sites/constants/contract-types';

import { FALLBACK_COLOR, initials, UNASSIGNED_COLOR } from '../constants/approColors';
import {
    formatVolume,
    motifFor,
    regroupementRankLabel,
    sumMetrics,
    type DynamicDeliveryRow,
    type PlanningTableRow,
} from '../constants/dynamicDeliveryColumns';

type Aggregate = { count: number; kg: number; volume: number };

/**
 * The contract belongs to the SITE, so it is read once on the group header rather than repeated on each of its
 * twelve rows. `null` when the group mixes contracts: a regroupement may gather neighbouring sites, and a
 * single badge would then lie about the others.
 */
const ContractHeaderTag = ({ rows }: { rows: DynamicDeliveryRow[] }) => {
    const contractTypes = new Set(rows.map((row) => row.site?.contractType).filter(Boolean));
    const [contractType] = [...contractTypes];
    if (contractTypes.size !== 1 || !contractType) return null;

    return <DetailFormStatusValue state={contractType} options={SITE_CONTRACT_TYPES_OPTIONS} />;
};

const distinctZones = (rows: DynamicDeliveryRow[]): { _id: string; name: string; color: string }[] => {
    const map = new Map<string, { _id: string; name: string; color: string }>();
    rows.forEach((r) => {
        if (r.deliveryZone) map.set(r.deliveryZone._id, r.deliveryZone);
    });
    return [...map.values()];
};

// The group lives in a single section (members are filtered by `isDeliver`), so a single count box is shown,
// labelled after the section the group belongs to.
const GroupStatBoxes = ({ agg, isDeliver }: { agg: Aggregate; isDeliver: boolean }) => (
    <Stats>
        <StatBox>
            <StatKey>{isDeliver ? 'À livrer' : 'Potentielles'}</StatKey>
            <StatVal>{agg.count}</StatVal>
        </StatBox>
        <StatBox>
            <StatKey>Poids</StatKey>
            <StatVal>{agg.kg.toFixed(1)} kg</StatVal>
        </StatBox>
        <StatBox>
            <StatKey>Volume</StatKey>
            <StatVal>{formatVolume(agg.volume)}</StatVal>
        </StatBox>
    </Stats>
);

const ZoneChipInline = ({ zone }: { zone: { name: string; color: string } }) => (
    <ZoneChip>
        <ZoneSwatch style={{ backgroundColor: zone.color }} />
        {zone.name}
    </ZoneChip>
);

const ApproHeader = ({
    rows,
    agg,
    isDeliver,
    approColors,
}: {
    rows: DynamicDeliveryRow[];
    agg: Aggregate;
    isDeliver: boolean;
    approColors: Map<string, string>;
}) => {
    const operator = rows[0]?.assignedDeliverer;
    const name = operator?.profile.fullname ?? 'Non assigné';
    const zones = distinctZones(rows);
    return (
        <HeadInner>
            <Avatar style={{ backgroundColor: approColors.get(operator?._id ?? '') ?? UNASSIGNED_COLOR }}>
                {initials(name)}
            </Avatar>
            <Ident>
                <Title>{name}</Title>
                <Sub>
                    {zones.map((zone) => (
                        <ZoneChipInline key={zone._id} zone={zone} />
                    ))}
                </Sub>
            </Ident>
            <GroupStatBoxes agg={agg} isDeliver={isDeliver} />
        </HeadInner>
    );
};

const SiteHeader = ({ rows, agg, isDeliver }: { rows: DynamicDeliveryRow[]; agg: Aggregate; isDeliver: boolean }) => {
    const site = rows[0]?.site;
    const zone = rows[0]?.deliveryZone ?? null;
    const referent = rows[0]?.referentDeliverer;
    return (
        <HeadInner>
            <Avatar style={{ backgroundColor: zone?.color ?? FALLBACK_COLOR }}>
                {(site?.name ?? '?').slice(0, 2).toUpperCase()}
            </Avatar>
            <Ident>
                <Title>{site?.name ?? '—'}</Title>
                <Sub>
                    {zone ? <ZoneChipInline zone={zone} /> : null}
                    <ContractHeaderTag rows={rows} />
                    <RefText>réf. {referent?.profile.fullname ?? '—'}</RefText>
                </Sub>
            </Ident>
            <GroupStatBoxes agg={agg} isDeliver={isDeliver} />
        </HeadInner>
    );
};

const RegroupementHeader = ({
    rows,
    agg,
    urgentCount,
}: {
    rows: DynamicDeliveryRow[];
    agg: Aggregate;
    urgentCount: number;
}) => {
    const rank = rows[0]?.rank ?? 0;
    const label = regroupementRankLabel(rank);
    const firstSiteName = rows[0]?.site?.name;
    return (
        <HeadInner>
            <Badge variant="secondary" style={{ fontWeight: 700 }}>
                {label}
            </Badge>
            {firstSiteName && <RegroupSite title={firstSiteName}>{firstSiteName}</RegroupSite>}
            <ContractHeaderTag rows={rows} />
            <SubMeta>
                {agg.count} colonne{agg.count > 1 ? 's' : ''}
            </SubMeta>
            {urgentCount > 0 && (
                <UrgentBadge title="Colonnes urgentes (Trop ancien · Critique)">
                    <AlertTriangle size={13} />
                    {urgentCount} urgente{urgentCount > 1 ? 's' : ''}
                </UrgentBadge>
            )}
            <SubStats>
                <span>
                    <b>{agg.kg.toFixed(1)}</b> kg
                </span>
                <span>
                    <b>{formatVolume(agg.volume)}</b> cm³
                </span>
            </SubStats>
        </HeadInner>
    );
};

const SectionHeader = ({ agg, isDeliver }: { agg: Aggregate; isDeliver: boolean }) => {
    return (
        <HeadInner>
            <StatusDot $deliver={isDeliver} />
            <SectionTitle>{isDeliver ? 'Colonnes à livrer' : 'Colonnes potentielles'}</SectionTitle>
            <SubMeta>
                {agg.count} colonne{agg.count > 1 ? 's' : ''}
            </SubMeta>
            <SubStats>
                <span>
                    <b>{agg.kg.toFixed(1)}</b> kg
                </span>
                <span>
                    <b>{formatVolume(agg.volume)}</b> cm³
                </span>
            </SubStats>
        </HeadInner>
    );
};

export const renderPlanningGroupRow = ({
    row,
    leafRows,
    columnCount,
    allRows,
    approColors,
}: {
    row: Row<PlanningTableRow>;
    leafRows: Row<PlanningTableRow>[];
    columnCount: number;
    allRows?: PlanningTableRow[];
    approColors: Map<string, string>;
}) => {
    const rows = leafRows.map((leaf) => leaf.original);
    const head = rows[0];
    const expanded = row.getIsExpanded();
    const groupId = row.groupingColumnId;
    const isSection = groupId === 'section';
    const isRegroupement = groupId === 'regroupement';
    const isAppro = groupId === 'operatorGroup' || groupId === 'siteGroup';
    const isDeliver = isSection ? row.getValue('section') === 'deliver' : (head?.isDeliver ?? false);

    const source = allRows ?? rows;
    const members = source.filter((r) => {
        if (isSection) return r.isDeliver === isDeliver;
        if (isRegroupement)
            return (
                r.isDeliver === isDeliver &&
                r.assignedDeliverer?._id === head?.assignedDeliverer?._id &&
                r.rank === head?.rank
            );
        const key = groupId === 'operatorGroup' ? head?.assignedDeliverer?._id : head?.site?._id;

        return (
            r.isDeliver === isDeliver && (groupId === 'operatorGroup' ? r.assignedDeliverer?._id : r.site?._id) === key
        );
    });
    // `members` is always filtered on ONE side of `isDeliver` (every branch above), so a single sum is the
    // whole story — no deliver/potential split to maintain here.
    const agg: Aggregate = { count: members.length, ...sumMetrics(members) };

    // Urgent = motif status 'critical' (Trop ancien / Critique) — same definition as the Motif column
    // and the summary cards. Surfaced on the regroupement header so it reads even when collapsed.
    const urgentCount = members.filter((r) => motifFor(r)?.status === 'critical').length;

    // The card bottom is normally closed by the appro's last leaf row. When a group is collapsed and is the last visible
    // row of its appro block (the appro itself, or its last regroupement), close the card on this header instead.
    const parentSubRows = row.getParentRow()?.subRows;
    const isLastChild = parentSubRows ? parentSubRows[parentSubRows.length - 1]?.id === row.id : false;
    const closeBottom = !expanded && (isAppro || (isRegroupement && isLastChild));

    const allSelected = row.getIsAllSubRowsSelected();
    const someSelected = !allSelected && row.getIsSomeSelected();

    return (
        <GroupCell
            colSpan={columnCount}
            $section={isSection}
            $appro={isAppro}
            $regroupement={isRegroupement}
            $deliver={isDeliver}
            $closeBottom={closeBottom}
            onClick={() => row.toggleExpanded()}
        >
            <GroupRowWrapper $depth={row.depth}>
                <CheckboxSlot onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                        checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                        onCheckedChange={(value) => row.toggleSelected(value === true)}
                        aria-label="Sélectionner le groupe"
                    />
                </CheckboxSlot>
                <Chevron $open={expanded}>
                    <ChevronRight size={isSection ? 18 : isRegroupement ? 14 : 16} />
                </Chevron>
                {isSection && <SectionHeader agg={agg} isDeliver={isDeliver} />}
                {groupId === 'operatorGroup' && (
                    <ApproHeader rows={rows} agg={agg} isDeliver={isDeliver} approColors={approColors} />
                )}
                {groupId === 'siteGroup' && <SiteHeader rows={rows} agg={agg} isDeliver={isDeliver} />}
                {isRegroupement && <RegroupementHeader rows={rows} agg={agg} urgentCount={urgentCount} />}
            </GroupRowWrapper>
        </GroupCell>
    );
};

const GroupCell = styled(TableCell)<{
    $section: boolean;
    $appro: boolean;
    $regroupement: boolean;
    $deliver: boolean;
    $closeBottom: boolean;
}>`
    cursor: pointer;
    user-select: none;
    background: ${({ theme, $section, $deliver }) =>
        $section
            ? $deliver
                ? `color-mix(in srgb, ${theme.successColor} 12%, transparent)`
                : theme.backgroundColor
            : theme.cardBackgroundColor};

    ${({ theme, $section }) => ($section ? `border-top: 1px solid ${theme.infoTextColor};` : '')}

    ${({ theme, $appro }) =>
        $appro
            ? `border-top: 1px solid ${theme.lightBorderColor};
               border-bottom: 1px solid ${theme.lightBorderColor};`
            : ''}

    ${({ theme, $regroupement }) => ($regroupement ? `border-bottom: 1px solid ${theme.lightBorderColor};` : '')}

    ${({ theme, $closeBottom }) => ($closeBottom ? `border-bottom: 1px solid ${theme.lightBorderColor};` : '')}
`;

const SectionTitle = styled.span`
    font-weight: 800;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: ${({ theme }) => theme.textColor};
`;

const StatusDot = styled.span<{ $deliver: boolean }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    background: ${({ theme, $deliver }) => ($deliver ? theme.successColor : theme.infoTextColor)};
`;

const GroupRowWrapper = styled.div<{ $depth: number }>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding-left: ${({ $depth }) => $depth * 20}px;
`;

const CheckboxSlot = styled.span`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
`;

const HeadInner = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
`;

const Avatar = styled.span`
    width: 30px;
    height: 30px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    font-weight: 700;
    font-size: 11px;
    color: #fff;
    flex-shrink: 0;
`;

const Ident = styled.div`
    min-width: 0;
`;

const Title = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${({ theme }) => theme.textColor};
`;

const Sub = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    flex-wrap: wrap;
`;

const RefText = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const ZoneChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    padding: 1px 8px 1px 6px;
    border-radius: 999px;
`;

const ZoneSwatch = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 3px;
    flex-shrink: 0;
`;

const Stats = styled.div`
    margin-left: auto;
    display: flex;
    gap: 7px;
`;

const StatBox = styled.div`
    background: ${({ theme }) => theme.backgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 7px;
    padding: 4px 10px;
    text-align: right;
    flex: 0 0 100px;
`;

const StatKey = styled.div`
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.infoTextColor};
    font-weight: 700;
`;

const StatVal = styled.div`
    font-weight: 700;
    font-size: 13px;
    color: ${({ theme }) => theme.textColor};
`;

const SubMeta = styled.span`
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const RegroupSite = styled.span`
    font-size: 13px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 240px;
`;

const UrgentBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    color: ${({ theme }) => theme.errorColor};
`;

const SubStats = styled.span`
    margin-left: auto;
    display: flex;
    gap: 16px;
    font-size: 12px;
    color: ${({ theme }) => theme.infoTextColor};

    b {
        color: ${({ theme }) => theme.textColor};
    }
`;

const Chevron = styled.span<{ $open: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.infoTextColor};
    transition: transform 0.18s ease;
    transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
    flex-shrink: 0;
`;
